/**
 * InvoiceReceivablesCheckDialog — Sales Control › Invoice › Receivables Check.
 * Converted 1:1 from Access `frmCtrlSalesQryInvoiceAdd_ReceivablesCheck`
 * (section "Receivables Check", btnSave = UPDATE public_invoice_scs rc_* columns).
 *
 * Editable fields (Access Left/Top order — 2-col, left Left=4332 / right Left=12984):
 *   Row1  (T=72)   ETA/ATA (date, right col)
 *   Row2  (T=492)  Tracking No. (text, right col)
 *   Row3  (T=912)  Docs Delivered Date (date, left) · Due Date (date, right)
 *   Row4  (T=1332) Paid Date (date, left) · Paid Amount (Format=Standard, right)
 *   Row5  (T=1752) Paid Details (text, left → span full width to fill grid)
 * No required fields / validation (Access btnSave has no strCheck / errCheck).
 * Access readonly context (Contract Info / Invoice Info DLookUp cells) omitted —
 * this dialog only edits the SaleInvoiceReceivablesWrite payload.
 */
import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle,
  Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type SaleInvoiceReceivablesWrite,
  useLazyGetSaleInvoiceFormQuery,
  useUpdateSaleInvoiceReceivablesMutation,
} from '../../store/api/saleInvoiceApi'
import { FORM_GRID_2COL } from '../../theme/responsive'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

interface Props {
  open: boolean
  invoiceId: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  rcEtaAta: string
  rcTrackingNo: string
  rcDocsDeliveredDate: string
  rcDueDate: string
  rcPaidDate: string
  rcPaidAmount: string
  rcPaidDetails: string
}

const EMPTY: Draft = {
  rcEtaAta: '', rcTrackingNo: '', rcDocsDeliveredDate: '', rcDueDate: '',
  rcPaidDate: '', rcPaidAmount: '', rcPaidDetails: '',
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const gridSx: SxProps<Theme> = {
  ...FORM_GRID_2COL, columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
/** Access Format="Standard" (`#,##0.00`) — 2 decimals + thousand separator, right-aligned. */
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))

export default function InvoiceReceivablesCheckDialog({ open, invoiceId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  const [loadForm] = useLazyGetSaleInvoiceFormQuery()
  const [updateReceivables, { isLoading: saving }] = useUpdateSaleInvoiceReceivablesMutation()

  // Hydrate rc_* from the current invoice form on open.
  useEffect(() => {
    if (!open || !invoiceId) return
    let cancelled = false
    void (async () => {
      const f = await loadForm(invoiceId).unwrap().catch(() => null)
      if (cancelled) return
      if (!f) { setDraft(EMPTY); return }
      const g = (k: string) => s((f as Record<string, unknown>)[k])
      setDraft({
        rcEtaAta: g('rcEtaAta').slice(0, 10),
        rcTrackingNo: g('rcTrackingNo'),
        rcDocsDeliveredDate: g('rcDocsDeliveredDate').slice(0, 10),
        rcDueDate: g('rcDueDate').slice(0, 10),
        rcPaidDate: g('rcPaidDate').slice(0, 10),
        rcPaidAmount: fmtStd(g('rcPaidAmount')),
        rcPaidDetails: g('rcPaidDetails'),
      })
    })()
    return () => { cancelled = true }
  }, [open, invoiceId, loadForm])

  const canSave = canEdit && !!invoiceId && !saving

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (!canSave || !invoiceId) return
    const payload: SaleInvoiceReceivablesWrite = {
      rcEtaAta: draft.rcEtaAta || null,
      rcTrackingNo: draft.rcTrackingNo || null,
      rcDocsDeliveredDate: draft.rcDocsDeliveredDate || null,
      rcDueDate: draft.rcDueDate || null,
      rcPaidDate: draft.rcPaidDate || null,
      rcPaidAmount: parseNum(draft.rcPaidAmount),
      rcPaidDetails: draft.rcPaidDetails || null,
    }
    try {
      await updateReceivables({ id: invoiceId, body: payload }).unwrap()
      snack.success('Receivables updated')
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Update receivables failed'))
    }
  }

  const dateField = (key: keyof Draft, label: string, tabIndex: number) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{ textField: { size: SM, fullWidth: true, InputLabelProps: { shrink: true }, inputProps: { tabIndex } } }}
    />
  )
  const textField = (key: keyof Draft, label: string, tabIndex: number) => (
    <TextField
      label={label} size={SM} fullWidth value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      inputProps={{ tabIndex }}
    />
  )

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>Receivables Check</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit receivables.</Alert>}
        {/* Single "Receivables Check" section — 2-col grid (Access left/right columns). */}
        <Box sx={gridSx}>
          <Box sx={{ gridColumn: { sm: 1 }, gridRow: { sm: 1 } }}>{dateField('rcEtaAta', 'ETA/ATA', 1)}</Box>
          <Box sx={{ gridColumn: { sm: 2 }, gridRow: { sm: 1 } }}>{textField('rcTrackingNo', 'Tracking No.', 2)}</Box>
          <Box sx={{ gridColumn: { sm: 1 }, gridRow: { sm: 2 } }}>{dateField('rcDocsDeliveredDate', 'Docs Delivered Date', 3)}</Box>
          <Box sx={{ gridColumn: { sm: 2 }, gridRow: { sm: 2 } }}>{dateField('rcDueDate', 'Due Date', 4)}</Box>
          <Box sx={{ gridColumn: { sm: 1 }, gridRow: { sm: 3 } }}>{dateField('rcPaidDate', 'Paid Date', 5)}</Box>
          <Box sx={{ gridColumn: { sm: 2 }, gridRow: { sm: 3 } }}>
            <TextField
              label="Paid Amount" size={SM} fullWidth type="text" value={draft.rcPaidAmount}
              onChange={(e) => set('rcPaidAmount', e.target.value)}
              onBlur={() => set('rcPaidAmount', fmtStd(draft.rcPaidAmount))}
              inputProps={{ ...numProps, tabIndex: 6 }}
            />
          </Box>
          {/* Lone last field → span full width to keep grid balanced (skill Bước 4). */}
          <Box sx={{ gridColumn: { sm: '1 / -1' }, gridRow: { sm: 4 } }}>{textField('rcPaidDetails', 'Paid Details', 7)}</Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
